// ============================================================
// Vuex module `clinica` (namespaced) — Fase 2
// ------------------------------------------------------------
// Estado transversal do módulo Clínica:
//  - sessão do operador + perfil (RBAC Admin vs Atendimento)
//  - contexto de clínica/unidade (persistido via vuex-persistedstate)
//  - badges ao vivo da sidebar (acompanhamento / confirmações)
// Dados por-tela (pacientes, agenda, financeiro) NÃO ficam aqui — são
// buscados pelos stub-services direto nas views, com filtros na URL.
// ============================================================
import { ClinicSession } from "@/services/clinica";

export const state = {
  initialized: false,
  operator: null,
  role: "atendimento", // 'admin' | 'atendimento'
  clinics: [],
  activeClinicId: null,
  units: [],
  selectedUnitId: null,
  badges: { acompanhamento: 0, confirmacoes: 0 }
};

export const mutations = {
  SET_OPERATOR(state, operator) {
    state.operator = operator;
    if (operator && operator.role) state.role = operator.role;
  },
  SET_CONTEXT(state, { clinics, units }) {
    state.clinics = clinics;
    state.units = units;
  },
  SET_ACTIVE_CLINIC(state, clinicId) {
    state.activeClinicId = clinicId;
  },
  SET_UNIT(state, unitId) {
    state.selectedUnitId = unitId;
  },
  SET_BADGES(state, badges) {
    state.badges = { ...state.badges, ...badges };
  },
  DECREMENT_BADGE(state, key) {
    if (state.badges[key] > 0) state.badges[key] -= 1;
  },
  SET_INITIALIZED(state, v) {
    state.initialized = v;
  }
};

const LEVELS = ["none", "view", "edit"];

const activeClinic = state =>
  state.clinics.find(c => c.id === state.activeClinicId) ||
  state.clinics[0] ||
  null;

// Papel na clínica ativa (US-C11); sem clínicas carregadas, o do operador.
const activeRole = state => {
  const clinic = activeClinic(state);
  return clinic && clinic.role ? clinic.role : state.role;
};

const can = state => (area, level = "view") => {
  const clinic = activeClinic(state);
  const granted =
    (clinic && clinic.permissions && clinic.permissions[area]) || "none";
  return LEVELS.indexOf(granted) >= LEVELS.indexOf(level);
};

export const getters = {
  operator: state => state.operator,
  clinics: state => state.clinics,
  activeClinic,
  activeClinicId: state => (activeClinic(state) || {}).id || null,
  role: activeRole,
  isAdmin: state => activeRole(state) === "admin",
  /** Nível na área para a clínica ativa: can('financeiro', 'view'). */
  can,
  /** É staff de alguma clínica? (deriva da sessão real — gate do módulo). */
  isStaff: state => !!(state.operator && state.operator.clinicStaff),
  /** Unidades só da clínica ativa. */
  units: state => {
    const clinic = activeClinic(state);
    return clinic
      ? state.units.filter(u => !u.clinicId || u.clinicId === clinic.id)
      : state.units;
  },
  selectedUnitId: state => state.selectedUnitId,
  currentUnit: (state, getters) =>
    getters.units.find(u => u.id === state.selectedUnitId) ||
    getters.units[0] ||
    null,
  badges: state => state.badges,
  /** RBAC: [área, nível] (US-C11) ou o legado 'admin' (exige perfil admin). */
  canAccess: state => required => {
    if (Array.isArray(required)) return can(state)(required[0], required[1]);
    if (!required || required === "atendimento") return true;
    return activeRole(state) === "admin";
  }
};

export const actions = {
  /** Carrega sessão (operador + clínicas/unidades) e badges (idempotente). */
  async init({ commit, state, getters, dispatch }) {
    const { data } = await ClinicSession.session();
    commit("SET_OPERATOR", data.operator);
    commit("SET_CONTEXT", { clinics: data.clinics, units: data.units });
    const clinics = data.clinics || [];
    if (!clinics.some(c => c.id === state.activeClinicId)) {
      commit("SET_ACTIVE_CLINIC", clinics[0] ? clinics[0].id : null);
    }
    const units = getters.units;
    if (!units.some(u => u.id === state.selectedUnitId)) {
      const preferred = units.find(
        u => data.operator && u.id === data.operator.unitId
      );
      commit("SET_UNIT", (preferred || units[0] || {}).id || null);
    }
    await dispatch("refreshBadges");
    commit("SET_INITIALIZED", true);
  },

  async refreshBadges({ commit }) {
    const { data } = await ClinicSession.badges();
    commit("SET_BADGES", data);
  },

  /** Troca a clínica ativa: recarrega a sessão (papel/permissões) e badges. */
  async selectClinic({ commit, dispatch }, clinicId) {
    commit("SET_ACTIVE_CLINIC", clinicId);
    commit("SET_UNIT", null);
    await dispatch("init");
  },

  /** Troca a unidade ativa (refiltra listas/agenda/financeiro nas views).
   *  IDs de unidade são UUID (string) — não coagir para Number. */
  selectUnit({ commit }, unitId) {
    commit("SET_UNIT", unitId);
  },

  decConfirmationsBadge({ commit }) {
    commit("DECREMENT_BADGE", "confirmacoes");
  }
};
